/**
* For fetching usage data (daily, monthly, yearly)
 * used across both Dashboard and Customer modules.
 *
 * This service dynamically adjusts API calls based on whether a customer ID is provided:
 * - If an "id" is passed  fetches usage data for that specific customer.
 * - If "id" is omitted  fetches aggregated "Total Power Usage" data across all customers (Dashboard/Home screen).
 *
 * Refer to the `services` folder inside the Shared module for further details.
 */


import { useCallback, useMemo } from 'react';
import { createApiClient } from '@/core/api';
import type {
  Customer,
  CreateCustomer,
  GetCustomersParams,
  GetCustomersResponse,
  CustomerStats,
  CustomerListingResponseItem,
  CustomerDetail,
  GetCustomersBillingParams,
  CustomerBillingResponse,
  UploadPOAPayload,
} from '../models/customer.model';
import type { TelemetryApiResponse } from '../models/telemetry.model';
import type {
  BexioContactMigrationParams,
  BexioContactMigrationResult,
} from '../models/bexioContactMigration.model';
import type { BackendResponse } from '@/shared/models/common.model';
import { buildBexioContactMigrationQuery } from '../utils/bexioContactMigration.utils';

import { CUSTOMER_ENDPOINTS } from '../constants';

export const useCustomerService = () => {
  const clients = useMemo(
    () => ({
      real: createApiClient({ isMock: false }),
      mock: createApiClient({ isMock: true }),
    }),
    []
  );

  // No-op: flat CustomerDetail model used for details/edit flows

  const getCustomers = useCallback(
    async (params: GetCustomersParams): Promise<GetCustomersResponse> => {
      const MOCK_ENABLED = false;
      const apiClient = MOCK_ENABLED ? clients.mock : clients.real;

      const response = await apiClient.get<GetCustomersResponse>(
        CUSTOMER_ENDPOINTS.USERS,
        {
          params,
        }
      );
      return response.data;
    },
    [clients.mock, clients.real]
  );

  // Get list of customers to display in the modal for selecting notification recipients
  const getCustomersLists = useCallback(
    async (params: GetCustomersParams): Promise<GetCustomersResponse> => {
      const MOCK_ENABLED = false;
      const apiClient = MOCK_ENABLED ? clients.mock : clients.real;

      const response = await apiClient.get<GetCustomersResponse>(
        CUSTOMER_ENDPOINTS.CUSTOMERS_LIST,
        {
          params,
        }
      );
      return response.data;
    },
    [clients.mock, clients.real]
  );

  const getCustomerStats = useCallback(async (): Promise<CustomerStats> => {
    const MOCK_ENABLED = false;
    const apiClient = MOCK_ENABLED ? clients.mock : clients.real;

    const response = await apiClient.get<CustomerStats>(
      CUSTOMER_ENDPOINTS.CUSTOMER_STATUS
    );
    return response.data;
  }, [clients.mock, clients.real]);

  const getCustomerById = useCallback(
    async (id: string): Promise<CustomerDetail> => {
      const MOCK_ENABLED = false;
      const apiClient = MOCK_ENABLED ? clients.mock : clients.real;

      const response = await apiClient.get(
        CUSTOMER_ENDPOINTS.CUSTOMER_BY_ID(id)
      );

      const raw = response.data.data;
      const mapped: CustomerDetail = {
        id: raw.Id,
        firstName: raw.FirstName ?? '',
        lastName: raw.LastName ?? '',
        email: raw.Email ?? '',
        city: raw.City,
        streetAddress: raw.StreetAddress,
        zipCode: raw.ZipCode,
        energyBalance: raw.EnergyBalance,
        meterStatus: raw.MeterStatus,
        deviceState: raw.DeviceState,
        adminCreated: raw.AdminCreated,
        deviceId: raw.DeviceId,
        subscriptionType: raw.SubscriptionType,
        accountStatus: raw.AccountStatus,
        subscriptionStatus: raw.SubscriptionStatus,
        subscriptionEndDate: raw.SubEndDate,
        subscriptionName: raw.SubscriptionName,
        autoRenewal: raw.AutoRenewal,
        stripeCustomerId: raw.CustomerId,
        energyProviderCustomerId: raw.EnergyProviderCustomerId,
        energyProviderName: raw.EnergyProviderName,
        energyProviderEmail: raw.EnergyProviderEmail,
        isCancelled: raw.IsSubscriptionCancelled || false,
        wifiSignal: raw.WiFiSignal || null,
        isPayoutDisabled:raw.IsPayoutDisabled,
        isVollmachtDocumentUploaded:raw.IsVollmachtDocumentUploaded,
        vollmachtUrl:raw.VollmachtUrl,
        firmwareVersion:raw.FirmwareVersion,
        meterManufacturer: raw.MeterManufacturer,
        meterModel: raw.MeterModel,
        isAdminDeleted :raw.IsAdminDeleted,
        isYearlyBillDisabled:raw.IsYearlyBillDisabled,
      };

      return mapped;
    },
    [clients.mock, clients.real]
  );

  const createCustomer = useCallback(
    async (customer: CreateCustomer) => {
      const MOCK_ENABLED = false;
      const apiClient = MOCK_ENABLED ? clients.mock : clients.real;

      const response = await apiClient.post(
        CUSTOMER_ENDPOINTS.CREATE_CUSTOMER,
        customer
      );
      return response.data;
    },
    [clients.mock, clients.real]
  );

  const updateCustomer = useCallback(
    async (
      id: string,
      customer: Partial<CustomerDetail | Customer>
    ): Promise<CustomerDetail> => {
      const MOCK_ENABLED = false;
      const apiClient = MOCK_ENABLED ? clients.mock : clients.real;
  
      // Map UI model (flat CustomerDetail or nested Customer) to backend DTO (PascalCase)
      const src = customer as Partial<
        Customer & CustomerDetail & Record<string, unknown>
      >;
  
      // Fallback: if id not provided, try to read from payload
      let targetId = id;
      if (!targetId) {
        const maybe = src as unknown as Record<string, unknown>;
        const maybeId = maybe['id'] ?? maybe['Id'];
        targetId = typeof maybeId === 'string' ? maybeId : '';
      }
      if (!targetId) {
        throw new Error('Missing id for updateCustomer');
      }
  
      const payload: Record<string, unknown> = {};
  
      if (src.firstName !== undefined) payload.FirstName = src.firstName;
      if (src.lastName !== undefined) payload.LastName = src.lastName;
      if (src.email !== undefined) payload.Email = src.email;
      if (src.energyProviderEmail !== undefined)
        payload.EnergyProviderEmail = src.energyProviderEmail;
  
      // address may come as flat fields or nested `address` object
      if (src.streetAddress !== undefined)
        payload.StreetAddress = src.streetAddress;
      else if (src.address?.street !== undefined)
        payload.StreetAddress = src.address.street;
  
      if (src.city !== undefined) payload.City = src.city;
      else if (src.address?.city !== undefined) payload.City = src.address.city;
  
      if (src.zipCode !== undefined) payload.ZipCode = src.zipCode;
      else if (src.address?.zipCode !== undefined)
        payload.ZipCode = src.address.zipCode;
  
      // include optional fields if present
      if (src.energyProviderName !== undefined)
        payload.EnergyProviderName = src.energyProviderName;
      if (src.customerNumber !== undefined)
        payload.EnergyProviderCustomerId = src.customerNumber;
  
      // payout flag
      if (src.isPayoutDisabled !== undefined)
        payload.isPayoutDisabled = src.isPayoutDisabled;
  
      if(src.isYearlyBillDisabled!== undefined)
        payload.isYearlyBillDisabled=src.isYearlyBillDisabled

      const response = await apiClient.put<CustomerListingResponseItem>(
        CUSTOMER_ENDPOINTS.UPDATE_CUSTOMER(targetId),
        payload
      );
  
      const raw = response.data;
      const mapped: CustomerDetail = {
        id: raw.Id,
        firstName: raw.FirstName ?? '',
        lastName: raw.LastName ?? '',
        email: raw.Email ?? '',
        city: raw.City,
        streetAddress: raw.StreetAddress,
        zipCode: raw.ZipCode,
        energyBalance: raw.EnergyBalance,
        meterStatus: raw.MeterStatus,
        deviceState: raw.DeviceState,
        adminCreated: raw.AdminCreated,
        deviceId: raw.DeviceId,
        subscriptionType: raw.SubscriptionType,
        accountStatus: raw.AccountStatus ?? undefined,
        isPayoutDisabled: raw.isPayoutDisabled,
        isYearlyBillDisabled:raw.isYearlyBillDisabled
 


      };
  
      return mapped;
    },
    [clients.mock, clients.real]
  );
  
  const cancelSubscription = useCallback(
    async (id: string): Promise<void> => {
      const MOCK_ENABLED = false;
      const apiClient = MOCK_ENABLED ? clients.mock : clients.real;

      await apiClient.post(CUSTOMER_ENDPOINTS.CANCEL_SUBSCRIPTION, {
        userId: id,
      });
    },
    [clients.mock, clients.real]
  );

  const getCustomerTelemetry = useCallback(
    async (
      id: string,
      params: Record<string, unknown> = {}
    ): Promise<TelemetryApiResponse> => {
      const MOCK_ENABLED = false;
      const apiClient = MOCK_ENABLED ? clients.mock : clients.real;

      const response = await apiClient.get(
        // Expected endpoint: /portal-admin/customers/{id}/telemetry
        // We'll reuse a generic CUSTOMER_ENDPOINTS.CUSTOMER_BY_ID for now and assume backend exposes telemetry at a known path.
        CUSTOMER_ENDPOINTS.CUSTOMER_TELEMETRY(id),
        { params }
      );

      // Expecting { data: Array<...>, page, limit, total }
      const payload = response.data ?? {};
      const data = Array.isArray(payload.data) ? payload.data : [];

      // Forward known pagination fields without altering casing so caller can pick them up
      return {
        data,
        page: payload.Page,
        limit: payload.Limit,
        total: payload.TotalCount,
        Page: payload.Page,
        Limit: payload.Limit,
        TotalCount: payload.TotalCount,
        TotalPageCount: payload.TotalPageCount,
      };
    },
    [clients.mock, clients.real]
  );
  const giftEnergy = useCallback(
    async (id: string, payload: { kWh: number }): Promise<void> => {
      const MOCK_ENABLED = false;
      const apiClient = MOCK_ENABLED ? clients.mock : clients.real;

      await apiClient.post(CUSTOMER_ENDPOINTS.GIFT_ENERGY(id), payload);
    },
    [clients.mock, clients.real]
  );

// Individual Customer's Billing History
const getcustomerBillng = useCallback(
  async (params:GetCustomersBillingParams): Promise<CustomerBillingResponse> => {
    const MOCK_ENABLED = false;
    const apiClient = MOCK_ENABLED ? clients.mock : clients.real;

    if (!params?.id) {
      throw new Error('Customer ID is required');
    }

    const response = await apiClient.get<CustomerBillingResponse>(
      CUSTOMER_ENDPOINTS.TRANSACTION_LISTING(params.id),
      {
        params: {
          page: params.page,
          limit: params.limit,
          type: params.type,
          startDate: params.startDate,
          endDate: params.endDate,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
        },
      }
    );

    return response.data;
  },
  [clients.mock, clients.real]
);

/** Upload Power of Attorney (POA) */
const uploadPOA = useCallback(
  async (payload:UploadPOAPayload) => {
    const MOCK_ENABLED = false;
    const apiClient = MOCK_ENABLED ? clients.mock : clients.real;

    const response = await apiClient.post(
      CUSTOMER_ENDPOINTS.UPLOAD_POA,
      payload
    );
    return response.data;
  },
  [clients.mock, clients.real]
);

/** Convert Bexio company contacts into persons (super admin only). Dry run unless dryRun is false. */
const convertBexioCompanyContacts = useCallback(
  async (
    params: BexioContactMigrationParams
  ): Promise<BexioContactMigrationResult> => {
    const response = await clients.real.post<
      BackendResponse<BexioContactMigrationResult>
    >(CUSTOMER_ENDPOINTS.BEXIO_CONVERT_COMPANY_CONTACTS, null, {
      params: buildBexioContactMigrationQuery(params),
    });
    return response.data.data;
  },
  [clients.real]
);

  return {
    getCustomers,
    getCustomersLists,
    getCustomerStats,
    getCustomerById,
    createCustomer,
    updateCustomer,
    cancelSubscription,
    getCustomerTelemetry,
    giftEnergy,
    getcustomerBillng,
    uploadPOA,
    convertBexioCompanyContacts,
  };
};

